import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Input from 'antd/lib/input';
import Empty from 'antd/lib/empty';
import Spin from 'antd/lib/spin';
import Skeleton from 'antd/lib/skeleton';
import { listArticles } from '../../api/articles';
import { mapApiArticleToCard } from '../../api/articles.mappers';
import ArticleItem from './ArticleItem';
import IconBadge from './IconBadge';

const { Search } = Input;

function SupportSidebar({ loadingSkeleton = false }) {
  const navigate = useNavigate();
  const [allPublishedArticles, setAllPublishedArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    async function loadPublishedArticles() {
      setLoading(true);
      try {
        const response = await listArticles({
          page: 1,
          pageSize: 100,
          status: 'PUBLISHED',
          sortBy: 'publishedAt',
          sortOrder: 'desc',
        });
        const mapped = response.items.map(mapApiArticleToCard);
        const sorted = mapped.sort(
          (a, b) => new Date(b.sortAt || 0).getTime() - new Date(a.sortAt || 0).getTime(),
        );
        setAllPublishedArticles(sorted);
      } catch {
        try {
          const fallbackResponse = await listArticles({
            page: 1,
            pageSize: 100,
          });
          const publishedArticles = fallbackResponse.items
            .map(mapApiArticleToCard)
            .filter((article) => article.status === 'Published')
            .sort((a, b) => new Date(b.sortAt || 0).getTime() - new Date(a.sortAt || 0).getTime());
          setAllPublishedArticles(publishedArticles);
        } catch {
          setAllPublishedArticles([]);
        }
      } finally {
        setLoading(false);
      }
    }

    loadPublishedArticles();
  }, []);

  useEffect(() => {
    const normalized = searchTerm.trim();
    if (!normalized) {
      setSearchResults([]);
      setIsSearchOpen(false);
      setSearchLoading(false);
      return undefined;
    }

    const timerId = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await listArticles({
          page: 1,
          pageSize: 8,
          status: 'PUBLISHED',
          q: normalized,
          sortBy: 'publishedAt',
          sortOrder: 'desc',
        });
        const mapped = response.items.map(mapApiArticleToCard).filter((item) => item.title);
        setSearchResults(mapped);
        setIsSearchOpen(true);
      } catch {
        setSearchResults([]);
        setIsSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 320);

    return () => window.clearTimeout(timerId);
  }, [searchTerm]);

  const filteredArticles = useMemo(() => allPublishedArticles.slice(0, 4), [allPublishedArticles]);

  const handleSearchResultClick = (article) => {
    const identifier = article.slug || article.backendId || article.id;
    setIsSearchOpen(false);
    if (!identifier) return;
    navigate(`/articles/${encodeURIComponent(identifier)}`);
  };

  if (loadingSkeleton) {
    return (
      <aside className="support-rail" aria-label="Help resources loading">
        <section className="help-search-card help-search-card--skeleton">
          <Skeleton active title={{ width: '56%' }} paragraph={false} />
          <Skeleton.Input active block />
        </section>

        <section className="popular-card popular-card--skeleton">
          <header className="popular-card__header">
            <Skeleton active title={{ width: 140 }} paragraph={false} />
          </header>
          <ul className="popular-card__list">
            {Array.from({ length: 4 }).map((_, index) => (
              <li key={`popular-skeleton-${index}`}>
                <Skeleton active avatar={{ shape: 'square', size: 26 }} title={{ width: '55%' }} paragraph={{ rows: 1 }} />
              </li>
            ))}
          </ul>
        </section>

      </aside>
    );
  }

  return (
    <aside className="support-rail" aria-label="Help resources">
      <section className="help-search-card" aria-labelledby="help-search-title">
        <h2 id="help-search-title">Search for articles</h2>
        <div className="help-search-card__box">
          <Search
            placeholder="Search for articles..."
            aria-label="Search articles"
            enterButton={false}
            value={searchTerm}
            onFocus={() => setIsSearchOpen(Boolean(searchTerm.trim()))}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          {isSearchOpen ? (
            <div className="help-search-dropdown" role="listbox" aria-label="Article search results">
              {searchLoading ? (
                <div className="help-search-dropdown__state">
                  <Spin size="small" />
                </div>
              ) : searchResults.length ? (
                <ul className="help-search-dropdown__list">
                  {searchResults.map((article) => (
                    <li key={article.id}>
                      <button
                        type="button"
                        className="help-search-dropdown__item"
                        onClick={() => handleSearchResultClick(article)}
                      >
                        <span className="help-search-dropdown__title">{article.title}</span>
                        <span className="help-search-dropdown__meta">
                          {article.category} • {article.author}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="help-search-dropdown__state">
                  <Empty description="No matching published articles" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <section className="popular-card" aria-labelledby="popular-title">
        <header className="popular-card__header">
          <h2 id="popular-title">Popular Articles</h2>
          <div className="popular-card__header-actions">
            <Link to="/articles" className="popular-card__see-all">
              See all
            </Link>
            <IconBadge icon="trend" className="popular-card__trend" />
          </div>
        </header>
        <ul className="popular-card__list">
          {loading ? (
            <li className="popular-card__state">
              <Spin size="small" />
            </li>
          ) : filteredArticles.length ? (
            filteredArticles.map((article) => <ArticleItem key={article.id} article={article} />)
          ) : (
            <li className="popular-card__state">
              <Empty description="No published articles" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </li>
          )}
        </ul>
      </section>
    </aside>
  );
}

export default SupportSidebar;
